import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as turf from '@turf/turf';
import { ActionType } from 'angular-cesium';
import { forkJoin, Observable, Subject } from 'rxjs';
import { catchError, map, mergeMap, reduce, tap } from 'rxjs/operators';
import { Constants } from 'src/app/app.constants';
import { ErrorHandleService } from './error-handle.service';
import { LoadingService } from './loading.service';

@Injectable({
    providedIn: 'root',
})
export class GlobeService {
    constructor(
        private http: HttpClient,
        private errorHandleService: ErrorHandleService,
        private loadingService: LoadingService,
        private constants: Constants
    ) {}

    private GetSampleDataAPI = `${this.constants.apiUrl}/Search/getsampledata`;
    private GetMeasurementDataAPI = `${this.constants.apiUrl}/Search/getmeasurementdata`;


    private pointInfo = new Subject<any>();
    private polygonSelectionInfo = new Subject<any>();
    private filterValue = new Subject<any>();
    private sampleCount = new Subject<any>();
    private measureCount = new Subject<any>();
    private loadingComplete = new Subject<any>();

    sendPointInfo(message) {
        this.pointInfo.next(message);
    }

    clearPointInfo() {
        this.pointInfo.next();
    }

    getPointInfo(): Observable<any> {
        return this.pointInfo.asObservable();
    }

    sendPolygonSelectionInfo(message) {
        this.polygonSelectionInfo.next(message);
    }

    clearPolygonSelectionInfo() {
        this.polygonSelectionInfo.next();
    }

    getPolygonSelectionInfo(): Observable<any> {
        return this.polygonSelectionInfo.asObservable();
    }

    sendfilterValue(message) {
        this.filterValue.next(message);
    }

    clearfilterValue() {
        this.filterValue.next();
    }

    getfilterValue(): Observable<any> {
        return this.filterValue.asObservable();
    }

    sendSampleCount(message) {
        this.sampleCount.next(message);
    }

    clearSampleCount() {
        this.sampleCount.next();
    }

    getSampleCount(): Observable<any> {
        return this.sampleCount.asObservable();
    }

    sendMeasureCount(message) {
        this.measureCount.next(message);
    }

    clearMeasureCount() {
        this.measureCount.next();
    }

    getMeasureCount(): Observable<any> {
        return this.measureCount.asObservable();
    }

    sendLoadingComplete(message) {
        this.loadingComplete.next(message);
    }

    clearLoadingComplete() {
        this.loadingComplete.next();
    }

    getLoadingComplete(): Observable<any> {
        return this.loadingComplete.asObservable();
    }

    getSeaAreas(): Observable<any> {
        const uri = decodeURIComponent(
            `${this.constants.apiUrl}/Search/getoceans`
        );
        return this.http.get<any>(uri).pipe(
            tap((_) => console.log('fetched all sea areas')),
            catchError(this.errorHandleService.handleError('getSeaAreas', []))
        );
    }

    getSampleDetail(filterOptions: any): Observable<any> {
        const uri = decodeURIComponent(
            `${this.constants.apiUrl}/Search/getSampleDetails`
        );
        const config = {
            headers: new HttpHeaders().set('Content-Type', 'application/json'),
        };
        return this.http.post(uri, JSON.stringify(filterOptions), config).pipe(
            tap((_) => console.log('fetched sample detail')),
            catchError(
                this.errorHandleService.handleError('getSampleDetail', [])
            )
        );
    }

    getGeographySampleDataSummary(filterOptions: any): Observable<any> {
        const uri = decodeURIComponent(
            `${this.constants.apiUrl}/Search/getgeographysampledatasummary`
        );
        const config = {
            headers: new HttpHeaders().set('Content-Type', 'application/json'),
        };
        return this.http.post(uri, JSON.stringify(filterOptions), config).pipe(
            tap((_) => console.log('fetched sample detail')),
            catchError(
                this.errorHandleService.handleError(
                    'getGeographySampleDataSummary',
                    []
                )
            )
        );
    }

    generateLinksArray(areaType: string, id: number) {
        const uri = decodeURIComponent(
            `./assets/data/samples/${areaType}-${id}-`
        );

        var result: string[] = [];

        var ids = [...Array(10).keys()];

        result = ids.map((element) => {
            return uri + element + '.json';
        });

        return result;
    }

    getFile(url: string) {
        return this.http.get<any>(url).pipe(
            map((response: any) => {
                return response.map((data) => {
                    return {
                        id: data.Id,
                        actionType: ActionType.ADD_UPDATE,
                        entity: {
                            id: data.Id,
                            position: Cesium.Cartesian3.fromDegrees(
                                data.Pos.split(',')[0],
                                data.Pos.split(',')[1]
                            ),
                        },
                    };
                });
            })
        );
    }

    getSampleData(areaType: string, id: number): Observable<any> {
        var urls = this.generateLinksArray(areaType, id);

        //return from(urls).mergeMap(url => this.getFile(url));
        var observables = urls.map((url) => this.getFile(url));
        return forkJoin(observables).pipe(
            mergeMap((list) => list),
            reduce((res, item) => res.concat(item), [])
        );
    }

    filterSamples(filterValue): Observable<any> {
        const config = {
            headers: new HttpHeaders().set('Content-Type', 'application/json'),
        };
        return this.http
            .post(this.GetSampleDataAPI, JSON.stringify(filterValue), config)
            .pipe(
                map((response: any) => {
                    if (response.length == 0) {
                        return null;
                    } else {
                        var point1 = turf.point(
                            response[0].pos.split(',').map((pos) => {
                                return parseFloat(pos);
                            })
                        );

                        var point2 = turf.point(
                            response[response.length - 1].pos
                                .split(',')
                                .map((pos) => {
                                    return parseFloat(pos);
                                })
                        );

                        var center = turf.midpoint(
                            turf.midpoint(
                                turf.midpoint(point1, point2),
                                point2
                            ),
                            point2
                        );

                        return {
                            target: center,
                            collection: response.map((data) => {
                                return {
                                    id: data.id,
                                    actionType: ActionType.ADD_UPDATE,
                                    entity: {
                                        id: data.id,
                                        position: Cesium.Cartesian3.fromDegrees(
                                            data.pos.split(',')[0],
                                            data.pos.split(',')[1]
                                        ),
                                        measureCount: data.measureCount,
                                    },
                                };
                            }),
                        };
                    }
                })
            );
    }

    filterMeasurementData(filterValue): Observable<any> {
        const config = {
            headers: new HttpHeaders().set('Content-Type', 'application/json'),
        };
        return this.http
            .post(this.GetMeasurementDataAPI, JSON.stringify(filterValue), config)
            .pipe(
                map((response: any) => {
                    if (response.length == 0) {
                        return null;
                    } else {
                        console.log(response)
                    }
                })
            );
    }
}
